import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// sessionStorage (not localStorage) so the admin password clears when the
// browser tab closes — meaningfully smaller XSS exposure window.
const PASSWORD_KEY = "alchemy_admin_password";

const emptyForm = {
    title: "",
    excerpt: "",
    body: "",
    category: "",
    tags: "",
    featured_image_url: "",
    is_published: true,
    published_date: "",
    display_order: 0,
};

const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return "";
    }
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return "";
    }
};

function PasswordGate({ onUnlock }) {
    const [pw, setPw] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const tryUnlock = async (e) => {
        e.preventDefault();
        if (!pw.trim()) return;
        setSubmitting(true);
        // Verify by attempting an authenticated no-op write probe — easiest: PUT to non-existent slug
        // which should return 401 for bad pw and 404 for good pw.
        try {
            const res = await axios.put(
                `${API}/blog/__probe_${Date.now()}`,
                {},
                { headers: { "x-admin-password": pw }, validateStatus: () => true }
            );
            if (res.status === 401) {
                toast.error("Incorrect password.");
            } else {
                sessionStorage.setItem(PASSWORD_KEY, pw);
                onUnlock(pw);
            }
        } catch {
            toast.error("Unable to verify password. Check your connection.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="admin-gate"
            className="min-h-[60vh] flex items-center justify-center pt-32 pb-20"
            style={{ background: "#FAFAF8" }}
        >
            <form
                onSubmit={tryUnlock}
                className="w-full max-w-md px-6"
                data-testid="admin-gate-form"
            >
                <p className="editorial-eyebrow text-center">Restricted</p>
                <h1 className="mt-4 text-center font-serif text-4xl text-charcoal leading-tight">
                    Alchemy Insights <em className="italic text-gold">— Admin</em>
                </h1>
                <div className="mx-auto mt-6" style={{ height: 2, width: 48, background: "#8A9E85" }} />
                <div className="mt-10">
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">Password</label>
                    <input
                        type="password"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        className="editorial-input"
                        placeholder="••••••••"
                        data-testid="admin-password-input"
                        autoFocus
                        required
                    />
                </div>
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="admin-password-submit"
                    className="mt-10 editorial-button-primary w-full justify-center disabled:opacity-60"
                >
                    {submitting ? "Verifying…" : "Enter"}
                </button>
            </form>
        </section>
    );
}

function PostForm({ password, initial, onSaved, onCancel }) {
    const editing = !!initial;
    const [form, setForm] = useState(() =>
        initial
            ? {
                  title: initial.title || "",
                  excerpt: initial.excerpt || "",
                  body: initial.body || "",
                  category: initial.category || "",
                  tags: (initial.tags || []).join(", "),
                  featured_image_url: initial.featured_image_url || "",
                  is_published: initial.is_published !== false,
                  published_date: toDatetimeLocal(initial.published_date),
                  display_order: Number(initial.display_order) || 0,
              }
            : emptyForm
    );
    const [saving, setSaving] = useState(false);

    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.body.trim()) {
            toast.error("Title and body are required.");
            return;
        }
        setSaving(true);
        const payload = {
            title: form.title.trim(),
            excerpt: form.excerpt.trim(),
            body: form.body,
            category: form.category.trim() || null,
            tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            featured_image_url: form.featured_image_url.trim() || null,
            is_published: form.is_published,
            display_order: Number(form.display_order) || 0,
        };
        if (form.published_date) {
            // datetime-local → ISO so the backend parses cleanly
            payload.published_date = new Date(form.published_date).toISOString();
        }
        try {
            if (editing) {
                await axios.put(`${API}/blog/${initial.slug}`, payload, {
                    headers: { "x-admin-password": password },
                });
                toast.success("Post updated.");
            } else {
                await axios.post(`${API}/blog`, payload, {
                    headers: { "x-admin-password": password },
                });
                toast.success("Post published.");
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={save}
            data-testid="admin-post-form"
            className="p-10 lg:p-12"
            style={{ background: "#FAFAF8", border: "1px solid #E2DDD8" }}
        >
            <div className="flex items-center justify-between mb-8">
                <h2 className="font-serif text-3xl text-charcoal">
                    {editing ? "Edit post" : "New post"}
                </h2>
                {editing && (
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="admin-cancel-edit"
                        className="text-[11px] tracking-[0.28em] uppercase gold-link"
                        style={{ color: "#9B7D5A" }}
                    >
                        Cancel
                    </button>
                )}
            </div>

            <div className="grid gap-8">
                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">Title</label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={set("title")}
                        className="editorial-input"
                        data-testid="admin-title-input"
                        required
                    />
                </div>

                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                        Excerpt <span className="normal-case tracking-normal text-charcoal-soft">(max 300)</span>
                    </label>
                    <input
                        type="text"
                        value={form.excerpt}
                        onChange={set("excerpt")}
                        maxLength={300}
                        className="editorial-input"
                        data-testid="admin-excerpt-input"
                    />
                </div>

                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">Body</label>
                    <textarea
                        value={form.body}
                        onChange={set("body")}
                        className="editorial-textarea"
                        rows={14}
                        style={{ minHeight: 320 }}
                        data-testid="admin-body-input"
                        required
                    />
                    <p className="mt-2 text-xs text-charcoal-soft italic">
                        Separate paragraphs with a blank line.
                    </p>
                </div>

                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                        Category <span className="normal-case tracking-normal text-charcoal-soft">(shown on cards & post header)</span>
                    </label>
                    <input
                        type="text"
                        value={form.category}
                        onChange={set("category")}
                        className="editorial-input"
                        data-testid="admin-category-input"
                        placeholder="EMDR, Anxiety, Healing…"
                    />
                </div>

                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                        Tags <span className="normal-case tracking-normal text-charcoal-soft">(comma separated)</span>
                    </label>
                    <input
                        type="text"
                        value={form.tags}
                        onChange={set("tags")}
                        className="editorial-input"
                        data-testid="admin-tags-input"
                        placeholder="Wisconsin, telehealth therapy, virtual therapy"
                    />
                </div>

                <div>
                    <label className="editorial-eyebrow text-charcoal/70 block mb-2">Featured Image URL</label>
                    <input
                        type="url"
                        value={form.featured_image_url}
                        onChange={set("featured_image_url")}
                        className="editorial-input"
                        data-testid="admin-image-input"
                        placeholder="https://…"
                    />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                            Publish date <span className="normal-case tracking-normal text-charcoal-soft">(controls ordering)</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={form.published_date}
                            onChange={set("published_date")}
                            className="editorial-input"
                            data-testid="admin-published-date-input"
                        />
                    </div>
                    <div>
                        <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                            Display order <span className="normal-case tracking-normal text-charcoal-soft">(higher pins higher)</span>
                        </label>
                        <input
                            type="number"
                            value={form.display_order}
                            onChange={set("display_order")}
                            className="editorial-input"
                            data-testid="admin-display-order-input"
                            placeholder="0"
                        />
                    </div>
                </div>

                <label className="flex items-center gap-3 text-charcoal cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.is_published}
                        onChange={set("is_published")}
                        data-testid="admin-publish-checkbox"
                    />
                    <span className="text-sm">Published (visible on /alchemy-insights)</span>
                </label>
            </div>

            <button
                type="submit"
                disabled={saving}
                data-testid="admin-save-button"
                className="mt-10 editorial-button-primary disabled:opacity-60"
            >
                {saving ? "Saving…" : editing ? "Save changes" : "Publish post"}
            </button>
        </form>
    );
}

function CommentsSettings({ password }) {
    const [settings, setSettings] = useState(null);
    const [enabled, setEnabled] = useState(true);
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/comments/settings`)
            .then((r) => {
                if (!alive) return;
                setSettings(r.data);
                setEnabled(!!r.data.enabled);
                setMessage(r.data.disabled_message || "");
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const save = async (next) => {
        setSaving(true);
        try {
            const payload =
                next !== undefined
                    ? { enabled: next, disabled_message: message }
                    : { enabled, disabled_message: message };
            const r = await axios.put(`${API}/comments/settings`, payload, {
                headers: { "x-admin-password": password },
            });
            setSettings(r.data);
            setEnabled(!!r.data.enabled);
            setMessage(r.data.disabled_message || "");
            toast.success("Comment settings updated.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update settings.");
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return null;

    return (
        <div
            data-testid="comments-settings-panel"
            className="p-8 lg:p-10 mb-10"
            style={{ background: "#FAFAF8", border: "1px solid #E2DDD8" }}
        >
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <p className="editorial-eyebrow">Comments</p>
                    <h2 className="mt-2 font-serif text-2xl text-charcoal">
                        Site-wide comments {enabled ? "are on" : "are paused"}
                    </h2>
                    <p className="mt-2 text-xs italic text-charcoal-soft">
                        Toggles visibility for every blog post. Existing comments are preserved.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => save(!enabled)}
                    disabled={saving}
                    data-testid="comments-toggle-button"
                    className="editorial-button-primary"
                    style={{
                        background: enabled ? "#7D2F3F" : "#2C2825",
                        borderColor: enabled ? "#7D2F3F" : "#2C2825",
                        padding: "12px 22px",
                    }}
                >
                    {saving ? "Saving…" : enabled ? "Turn comments off" : "Turn comments on"}
                </button>
            </div>

            <div className="mt-8">
                <label className="editorial-eyebrow text-charcoal/70 block mb-2">
                    Message shown to visitors when comments are off
                </label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    maxLength={600}
                    data-testid="comments-disabled-message-input"
                    className="editorial-textarea"
                    style={{ minHeight: 90 }}
                />
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={() => save()}
                        disabled={saving}
                        data-testid="comments-save-message"
                        className="text-[11px] tracking-[0.24em] uppercase gold-link"
                        style={{ color: "#9B7D5A" }}
                    >
                        {saving ? "Saving…" : "Save message"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PostsList({ password, posts, onEdit, onDeleted, onChanged }) {
    const del = async (slug) => {
        if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/blog/${slug}`, {
                headers: { "x-admin-password": password },
            });
            toast.success("Deleted.");
            onDeleted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete.");
        }
    };

    const bumpOrder = async (post, delta) => {
        const next = (Number(post.display_order) || 0) + delta;
        try {
            await axios.put(
                `${API}/blog/${post.slug}`,
                { display_order: next },
                { headers: { "x-admin-password": password } }
            );
            onChanged && onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update order.");
        }
    };

    if (posts === null) return <p className="text-charcoal-soft">Loading…</p>;
    if (posts.length === 0)
        return (
            <p className="text-center font-serif italic py-12" style={{ color: "#8A7968" }}>
                No posts yet. Create the first one.
            </p>
        );

    return (
        <ul className="divide-y" style={{ borderColor: "#E2DDD8" }} data-testid="admin-posts-list">
            {posts.map((p) => (
                <li
                    key={p.id}
                    data-testid={`admin-post-row-${p.slug}`}
                    className="py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                    style={{ borderColor: "#E2DDD8" }}
                >
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "#9B7D5A" }}>
                            {formatDate(p.published_date)} · /{p.slug}
                            {p.category && (
                                <span className="ml-3" style={{ color: "#8A7968" }}>
                                    {p.category}
                                </span>
                            )}
                            {!p.is_published && (
                                <span className="ml-3 px-2 py-0.5 text-charcoal" style={{ background: "#F2EDE6" }}>
                                    DRAFT
                                </span>
                            )}
                            {Number(p.display_order) > 0 && (
                                <span
                                    data-testid={`admin-post-order-${p.slug}`}
                                    className="ml-3 px-2 py-0.5"
                                    style={{ background: "#F4E6D9", color: "#7D2F3F" }}
                                >
                                    ORDER {p.display_order}
                                </span>
                            )}
                        </p>
                        <h3 className="mt-1 font-serif text-xl text-charcoal truncate">{p.title}</h3>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="flex items-center" data-testid={`admin-post-reorder-${p.slug}`}>
                            <button
                                onClick={() => bumpOrder(p, 1)}
                                title="Move up (raise display order)"
                                aria-label="Move up"
                                className="text-charcoal px-2"
                                style={{ fontSize: 14, lineHeight: 1 }}
                            >
                                ▲
                            </button>
                            <button
                                onClick={() => bumpOrder(p, -1)}
                                title="Move down (lower display order)"
                                aria-label="Move down"
                                className="text-charcoal px-2"
                                style={{ fontSize: 14, lineHeight: 1 }}
                            >
                                ▼
                            </button>
                        </div>
                        <a
                            href={`/alchemy-insights/${p.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] tracking-[0.24em] uppercase gold-link"
                            style={{ color: "#9B7D5A" }}
                        >
                            View
                        </a>
                        <button
                            onClick={() => onEdit(p)}
                            data-testid={`admin-edit-${p.slug}`}
                            className="text-[11px] tracking-[0.24em] uppercase gold-link"
                            style={{ color: "#2C2825" }}
                        >
                            Edit
                        </button>
                        <button
                            onClick={() => del(p.slug)}
                            data-testid={`admin-delete-${p.slug}`}
                            className="text-[11px] tracking-[0.24em] uppercase gold-link"
                            style={{ color: "#997777" }}
                        >
                            Delete
                        </button>
                    </div>
                </li>
            ))}
        </ul>
    );
}

export default function AlchemyInsightsAdminPage() {
    const [password, setPassword] = useState(() => sessionStorage.getItem(PASSWORD_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [posts, setPosts] = useState(null);
    const [editing, setEditing] = useState(null);

    const fetchPosts = async () => {
        try {
            const r = await axios.get(`${API}/blog?include_unpublished=true`);
            setPosts(r.data);
        } catch {
            setPosts([]);
        }
    };

    // Verify saved password on mount
    useEffect(() => {
        if (!password) return;
        let alive = true;
        (async () => {
            const res = await axios.put(
                `${API}/blog/__probe_${Date.now()}`,
                {},
                { headers: { "x-admin-password": password }, validateStatus: () => true }
            );
            if (!alive) return;
            if (res.status === 401) {
                sessionStorage.removeItem(PASSWORD_KEY);
                setPassword("");
            } else {
                setAuthed(true);
                fetchPosts();
            }
        })();
        return () => { alive = false; };
    }, [password]);

    const handleUnlock = (pw) => {
        setPassword(pw);
        setAuthed(true);
        fetchPosts();
    };

    const signOut = () => {
        sessionStorage.removeItem(PASSWORD_KEY);
        setPassword("");
        setAuthed(false);
        setPosts(null);
    };

    return (
        <main className="bg-ivory text-charcoal" data-testid="alchemy-insights-admin-page">
            <Seo title="Admin | Alchemy Insights" description="Admin." />
            <Nav />

            {!authed ? (
                <PasswordGate onUnlock={handleUnlock} />
            ) : (
                <section className="pt-32 pb-24" style={{ background: "#FAFAF8" }}>
                    <div className="mx-auto max-w-[1100px] px-6 lg:px-12">
                        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                            <div>
                                <p className="editorial-eyebrow">Admin</p>
                                <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-charcoal">
                                    Alchemy Insights <em className="italic text-gold">— Editor</em>
                                </h1>
                            </div>
                            <button
                                onClick={signOut}
                                data-testid="admin-sign-out"
                                className="text-[11px] tracking-[0.28em] uppercase gold-link"
                                style={{ color: "#9B7D5A" }}
                            >
                                Sign out
                            </button>
                        </div>

                        <div className="grid lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-12">
                                <CommentsSettings password={password} />
                            </div>
                            <div className="lg:col-span-7">
                                <PostForm
                                    key={editing ? editing.id : "new"}
                                    password={password}
                                    initial={editing}
                                    onSaved={() => {
                                        setEditing(null);
                                        fetchPosts();
                                    }}
                                    onCancel={() => setEditing(null)}
                                />
                            </div>
                            <div className="lg:col-span-5">
                                <div
                                    className="p-8 lg:p-10"
                                    style={{ background: "#F2EDE6", border: "1px solid #E2DDD8" }}
                                >
                                    <h2 className="font-serif text-2xl text-charcoal mb-2">Existing posts</h2>
                                    <p className="text-xs text-charcoal-soft italic mb-6">
                                        Drafts and published together.
                                    </p>
                                    <PostsList
                                        password={password}
                                        posts={posts}
                                        onEdit={(p) => setEditing(p)}
                                        onDeleted={fetchPosts}
                                        onChanged={fetchPosts}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
            <Footer />
        </main>
    );
}
